/* Orders — placement, quote, list, cancel. Backed by T-301 + T-302 + the
 * customer-PWA infill (T-301 added GET list, GET single, POST /orders/quote).
 *
 * Wire contract:
 *   - Backend body shape: `{ branch_id, items: [{variant_id, quantity}],
 *     delivery_address, payment_mode, scheduled_for? }`.
 *   - Frontend body shape (what screens pass in): `{ branchId, items:
 *     [{productId, variantLabel, qty, pricePaise}], addressId, slot,
 *     paymentMode, quoteId }`.
 *   - This module translates: camelCase → snake_case keys, items collapse
 *     to `{variant_id, quantity}` (`variantLabel` is dropped because the
 *     backend only needs the variant id — the price snapshot is fetched
 *     server-side), and `addressId` is resolved to a `delivery_address`
 *     JSON blob by looking up the customer's saved addresses.
 *   - Cancellation: frontend uses `POST /orders/{id}/cancel`; backend
 *     exposes `DELETE /orders/{id}`. We send a DELETE under the hood while
 *     keeping the higher-level `cancelOrder(id, ...)` signature.
 *
 * Known gaps:
 *   - The frontend items pass `productId` + `variantLabel`, NOT a
 *     `variant_id`. To map productId+variantLabel → variant_id we need a
 *     cart-builder that fetched real backend variants. The screen layer
 *     should pass `variantId` directly post-wiring — until then, callers
 *     that only have a legacy mock cart (productId + variantLabel) will
 *     fail at /orders/quote with 400 "unknown variant_id". Flagged for
 *     the StoreScreens/CheckoutScreens upgrade.
 *   - The backend `OrderQuote` returns flat totals; the frontend used to
 *     render an open-ended `lineItems[]`. We translate by synthesising one
 *     `gst` line item and one `delivery` line item from the flat values so
 *     existing rendering code keeps working.
 */

import { api, mockOr } from '../lib/api.js';
import { ApiError } from '../lib/errors.js';
import { TAX, DEL_FEE } from '../tokens.js';
import { D } from '../mock-data.js';
import { listAddresses } from './addresses.js';

/**
 * @typedef {Object} BillLineItem
 * @property {string} code
 * @property {string} label
 * @property {string} [hint]
 * @property {number} amountPaise
 * @property {'charge'|'tax'|'discount'} kind
 *
 * @typedef {Object} Bill
 * @property {'INR'} currency
 * @property {number} subtotalPaise
 * @property {BillLineItem[]} lineItems
 * @property {number} totalPaise
 * @property {string} quoteId
 * @property {number} quoteExpiresAt
 */

/** Resolve a saved-address id to the JSON shape the backend stores on
 *  `orders.delivery_address`. The backend requires at least `{line1, pincode}`.
 *  Throws a validation ApiError if the addressId doesn't match a known address. */
async function resolveDeliveryAddress(addressId) {
  if (!addressId) {
    throw new ApiError({
      code: 'validation',
      message: 'addressId is required',
      status: 422,
    });
  }
  const addresses = await listAddresses();
  const a = addresses.find((x) => x.id === addressId);
  if (!a) {
    throw new ApiError({
      code: 'validation',
      message: 'Selected address not found',
      status: 422,
    });
  }
  return {
    line1: a.line1 || a.full || '',
    line2: a.line2 || undefined,
    building_name: a.buildingName || undefined,
    pincode: a.pincode || '',
    city_id: a.cityId || undefined,
    lat: a.lat,
    lng: a.lng,
    delivery_instructions: a.deliveryInstructions || undefined,
    label: a.label || undefined,
  };
}

/** Frontend cart-item → backend order-line. Requires items to carry a
 *  `variantId` field (post-catalogue-wiring). Legacy items with only
 *  `productId`+`variantLabel` cause a validation error. */
function toBackendItems(items) {
  return items.map((i, idx) => {
    const variantId = i.variantId || i.variant_id;
    if (!variantId) {
      throw new ApiError({
        code: 'validation',
        message: `Cart item ${idx + 1} is missing variantId — re-add it from the catalogue.`,
        status: 422,
      });
    }
    return { variant_id: variantId, quantity: i.qty ?? i.quantity ?? 1 };
  });
}

/** Backend `OrderQuote` (flat totals) → frontend `Bill` (with lineItems). */
function quoteToBill(q) {
  const subtotalPaise = q.subtotal_paise;
  const lineItems = [];
  if (q.delivery_fee_paise > 0) {
    lineItems.push({
      code: 'delivery',
      label: 'Delivery fee',
      amountPaise: q.delivery_fee_paise,
      kind: 'charge',
    });
  }
  if (q.tax_paise > 0) {
    lineItems.push({
      code: 'gst',
      label: 'GST',
      amountPaise: q.tax_paise,
      kind: 'tax',
    });
  }
  return {
    currency: 'INR',
    subtotalPaise,
    lineItems,
    totalPaise: q.total_paise,
    // Quote is stateless server-side — we synthesise a local id so the
    // checkout flow can reference it; backend doesn't validate it back.
    quoteId: 'q-' + Date.now(),
    quoteExpiresAt: Date.now() + 5 * 60 * 1000,
  };
}

export async function quoteBill({ branchId, items, addressId, slot, paymentMode }) {
  return mockOr(
    async () => {
      const delivery_address = await resolveDeliveryAddress(addressId);
      const q = await api.post(
        '/orders/quote',
        {
          branch_id: branchId,
          items: toBackendItems(items),
          delivery_address,
          payment_mode: paymentMode || 'cod',
          scheduled_for: slot || undefined,
        },
        { idempotencyScope: 'orders.quote' },
      );
      return quoteToBill(q);
    },
    () => {
      const subtotalPaise = items.reduce(
        (sum, i) => sum + (i.pricePaise ?? i.priceP ?? 0) * (i.qty ?? 1),
        0,
      );
      const gstPaise = Math.round(subtotalPaise * TAX);
      const lineItems = [
        { code: 'delivery', label: 'Delivery fee', amountPaise: DEL_FEE, kind: 'charge' },
        { code: 'gst', label: `GST (${TAX * 100}%)`, amountPaise: gstPaise, kind: 'tax' },
      ];
      const totalPaise = subtotalPaise + lineItems.reduce((s, li) => s + li.amountPaise, 0);
      return {
        currency: 'INR',
        subtotalPaise,
        lineItems,
        totalPaise,
        quoteId: 'q-mock-' + Date.now(),
        quoteExpiresAt: Date.now() + 5 * 60 * 1000,
      };
    },
  );
}

/** Backend order row → frontend shape. snake_case → camelCase, items folded
 *  back into the order object so screens can render line-by-line. */
function toCamelOrder(o, items) {
  return {
    id: o.id,
    orderNumber: o.order_number,
    customerUserId: o.customer_user_id,
    supplierId: o.supplier_id,
    branchId: o.branch_id,
    status: o.status,
    subtotalPaise: o.subtotal_paise,
    taxPaise: o.tax_paise,
    deliveryFeePaise: o.delivery_fee_paise,
    totalPaise: o.total_paise,
    paymentMode: o.payment_mode,
    deliveryAddress: o.delivery_address,
    scheduledFor: o.scheduled_for,
    placedAt: o.placed_at,
    confirmedAt: o.confirmed_at,
    deliveredAt: o.delivered_at,
    cancelledAt: o.cancelled_at,
    items: (items || []).map((it) => ({
      id: it.id,
      productVariantId: it.product_variant_id,
      productName: it.product_name_snapshot,
      variantName: it.variant_name_snapshot,
      unitPricePaise: it.unit_price_paise,
      taxRateBps: it.tax_rate_bps_snapshot,
      qty: it.quantity,
      lineTotalPaise: it.line_total_paise,
    })),
  };
}

export async function placeOrder({ branchId, items, addressId, slot, paymentMode }) {
  return mockOr(
    async () => {
      const delivery_address = await resolveDeliveryAddress(addressId);
      const result = await api.post(
        '/orders',
        {
          branch_id: branchId,
          items: toBackendItems(items),
          delivery_address,
          payment_mode: paymentMode || 'cod',
          scheduled_for: slot || undefined,
        },
        { idempotencyScope: 'orders.place' },
      );
      // Backend returns { order, items }.
      return toCamelOrder(result.order, result.items);
    },
    () => {
      const oos = items.filter((i) => i.qty >= 5).map((i) => ({ productId: i.productId, available: 4 }));
      if (oos.length) {
        throw new ApiError({
          code: 'out_of_stock',
          message: 'Stock changed while you were checking out',
          status: 409,
          details: { items: oos },
        });
      }
      return {
        id: 'ORD-' + Math.floor(1000 + Math.random() * 9000),
        status: paymentMode === 'cod' ? 'confirmed' : 'pending_payment',
      };
    },
  );
}

export async function listOrders() {
  return mockOr(
    async () => {
      const rows = await api.get('/orders');
      return (rows || []).map((o) => toCamelOrder(o, []));
    },
    () => D.orders,
  );
}

export async function getOrder(orderId) {
  return mockOr(
    async () => {
      const r = await api.get(`/orders/${orderId}`);
      return toCamelOrder(r.order, r.items);
    },
    () => {
      const o = D.orders.find((x) => x.id === orderId);
      if (!o) throw new ApiError({ code: 'not_found', message: 'Order not found', status: 404 });
      return o;
    },
  );
}

/** Customer cancellation. Backend exposes `DELETE /orders/{id}`; the
 *  high-level signature retains `(id, { reason })` even though the reason
 *  isn't currently persisted server-side (v1 keeps the body shape forward-
 *  compatible for when supplier-side reason tracking lands). */
export async function cancelOrder(orderId, _opts = {}) {
  return mockOr(
    async () => {
      const o = await api.del(`/orders/${orderId}`);
      return toCamelOrder(o, []);
    },
    () => ({ ok: true }),
  );
}
